package dev.youndie.site.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import dev.youndie.site.components.layouts.PageLayoutData
import dev.youndie.site.components.widgets.Chip
import dev.youndie.site.components.widgets.Prompt
import dev.youndie.site.components.widgets.SectionHead
import dev.youndie.site.components.widgets.StackRow
import dev.youndie.site.components.widgets.TimelineEntry
import dev.youndie.site.model.AUTHOR_NAME
import dev.youndie.site.model.EMAIL
import dev.youndie.site.model.GITHUB
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@InitRoute
fun initAboutPage(ctx: InitRouteContext) {
    ctx.data.add(
        PageLayoutData(
            // "about" alone matches nothing. The words worth ranking for are the ones
            // a person hiring for this would type.
            title = "$AUTHOR_NAME — Kotlin Multiplatform and Android developer, CV",
            socialTitle = "$AUTHOR_NAME — CV",
            description = "Pavel Votyakov — Kotlin developer, 14 years across mobile and backend. " +
                "Lead Android on a Kotlin Multiplatform banking app; Ktor, Kotlin/Native and " +
                "Compose Multiplatform on his own projects.",
        )
    )
}

private class Job(
    val when_: String,
    val role: String,
    val org: String,
    val bullets: List<String>,
)

/**
 * Paid employment only. The retail platform is three years of real work but it is
 * mine and it has never earned anything, so it sits in its own section below rather
 * than in a list a reader will assume is a list of employers.
 */
private val JOBS = listOf(
    Job(
        when_ = "Jan 2026 — present",
        role = "Lead Android Developer",
        org = "Ipak Yuli Bank · banking, Tashkent",
        bullets = listOf(
            "Own the architecture and technical strategy of a mobile banking app built on Kotlin Multiplatform.",
            "Rewrote the build setup end-to-end: Kotlin DSL and precompiled gradle.kts script plugins, with unified dependency management.",
            "Led the migration to Koin for DI and shipped major releases with generated functional documentation.",
            "Set the bar for code quality, memory usage, security boundaries and API integration.",
        ),
    ),
    Job(
        when_ = "Dec 2021 — Nov 2022",
        role = "Android Engineer",
        org = "Osome · corporate services & document workflow, Singapore",
        bullets = listOf(
            "Refactored an international enterprise app into a strict multi-module architecture.",
            "Built CI/CD automation and custom Gradle plugins that generate and parse API client files while preserving local modifications.",
            "Shipped bank-connection integration, and migrated financial reporting and dashboard UI to Jetpack Compose.",
        ),
    ),
    Job(
        when_ = "Jun 2018 — May 2021",
        role = "Lead Android Developer",
        org = "SKB-Lab · banking",
        bullets = listOf(
            "Led the move of the bank's mobile client to a multi-module structure, cutting CI build times substantially.",
            "Integrated Google Pay and Samsung Pay, and designed secure early loan repayment flows.",
            "Built the home screen and the operations feed for corporate clients, and adapted the interface to Material 3.",
            "Built geolocation and status-tracking features for field-service apps.",
        ),
    ),
    Job(
        when_ = "Feb 2012 — 2020",
        role = "Mobile Developer (Android / iOS)",
        org = "work-in-progress, Kontur, TANDEM and others",
        bullets = listOf(
            "Full lifecycle of a wide range of mobile apps — system design, UI, testing, store review, support.",
        ),
    ),
)

private val RETAIL_PLATFORM = Job(
    when_ = "Jan 2023 — present",
    role = "Multi-tenant retail platform",
    org = "work-in-progress · my own product",
    bullets = listOf(
        "Ktor and PostgreSQL backend serving many shops from one deployment, with instances provisioned through the Java Kubernetes client.",
        "A Telegram bot as the primary interface rather than an afterthought: sales, returns, stocktaking, reservations and reports as stepwise flows.",
        "Compose Multiplatform storefront for Android and desktop over the same REST contract — live stock, filters, modifications, reservations.",
        "Next.js admin behind a Ktor backend, service-to-service auth through Keycloak (OAuth2 + JWT), tokens encrypted against Kubernetes secrets.",
        "CI/CD, schema migrations, logging and monitoring across all three.",
    ),
)

private class StackGroup(val label: String, val items: List<String>)

private val STACK = listOf(
    StackGroup(
        "language",
        listOf("Kotlin", "Kotlin Multiplatform", "Kotlin/Native", "Coroutines", "kotlinx.serialization"),
    ),
    StackGroup(
        "backend",
        listOf("Ktor", "Exposed", "PostgreSQL", "SQLite", "MongoDB", "Keycloak"),
    ),
    StackGroup(
        "clients",
        listOf("Android", "Jetpack Compose", "Compose Multiplatform", "Compose Desktop", "Compose HTML", "Next.js"),
    ),
    StackGroup(
        "infrastructure",
        listOf("Docker", "Kubernetes", "Helm", "Traefik", "GitHub Actions"),
    ),
    StackGroup(
        "build & tooling",
        listOf("Gradle (Kotlin DSL, custom plugins)", "KSP", "Koin", "MCP"),
    ),
)

@Page
@Composable
fun AboutPage() {
    Section(attrs = { classes("hero") }) {
        Prompt("cat about.md", path = "~")
        H1 { Text("about") }
        Div(attrs = { classes("role") }) { Text("remote · kotlin end to end") }

        P {
            Text(
                "Kotlin developer, 14 years across mobile and backend. Day job is leading Android on " +
                    "a Kotlin Multiplatform banking app; on my own time it is a multi-tenant retail " +
                    "platform — Ktor and Postgres underneath, a Telegram bot as the main interface, " +
                    "a Compose Multiplatform storefront on top."
            )
        }
        P {
            Text(
                "Outside of that: a set of open-source Kotlin projects — observability tooling on " +
                    "Kotlin/Native, a saga engine and a message broker on the JVM, testing and UI " +
                    "libraries for Compose Multiplatform."
            )
        }
        Div(attrs = { classes("hero-links") }) {
            Chip("/", "libraries")
            Chip(GITHUB, "github/youndie")
            Chip("mailto:$EMAIL", EMAIL)
        }
    }

    Section(attrs = { classes("section") }) {
        SectionHead("experience", "14 years")
        JOBS.forEach { job ->
            TimelineEntry(job.when_, job.role, job.org, job.bullets)
        }
    }

    Section(attrs = { classes("section") }) {
        SectionHead("side project", "3 years")
        TimelineEntry(
            RETAIL_PLATFORM.when_,
            RETAIL_PLATFORM.role,
            RETAIL_PLATFORM.org,
            RETAIL_PLATFORM.bullets,
        )
    }

    Section(attrs = { classes("section") }) {
        SectionHead("education")
        TimelineEntry(
            when_ = "2016",
            role = "BSc, Applied Informatics",
            org = "Ural Federal University · Institute of Mathematics and Computer Sciences",
            bullets = emptyList(),
        )
    }

    Section(attrs = { classes("section") }) {
        SectionHead("stack")
        STACK.forEach { group ->
            StackRow(group.label, group.items)
        }
    }
}
